// Tenant branding is a runtime swap of CSS custom properties — no per-tenant build. The brand
// colours are set inline on <html> here, which outranks every stylesheet; a company assigned a
// theme (shared/themes.js) also gets that theme's stylesheet linked, for everything else about
// how the product looks.

import { themeHref } from '../../../shared/themes.js'

function parseHex(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim())
  if (!m) return null
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
}

const toHex = (rgb) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`
const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t)

export function applyBranding(branding) {
  if (!branding) return
  const root = document.documentElement
  const primary = parseHex(branding.brandPrimary) || [31, 78, 121]
  const accent = parseHex(branding.brandAccent) || [240, 162, 2]

  root.style.setProperty('--brand-primary', toHex(primary))
  root.style.setProperty('--brand-accent', toHex(accent))
  root.style.setProperty('--brand-on-primary', branding.brandOnPrimary || '#ffffff')
  // Tints derived rather than configured: one colour in, a usable set out, so a tenant
  // cannot pick a combination that makes its own chrome unreadable.
  root.style.setProperty('--brand-tint', toHex(mix(primary, [255, 255, 255], 0.9)))
  root.style.setProperty('--brand-tint-strong', toHex(mix(primary, [255, 255, 255], 0.75)))
  root.style.setProperty('--brand-dark', toHex(mix(primary, [0, 0, 0], 0.25)))
  root.style.setProperty('--accent-tint', toHex(mix(accent, [255, 255, 255], 0.85)))

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', toHex(primary))

  applyTheme(branding.themeSlug)
}

const THEME_LINK_ID = 'tenant-theme'

/**
 * Link the stylesheet for a theme, swap it for another, or take it away for the default look.
 *
 * The href comes from themeHref, which returns null for anything not in the registry — so a
 * stale or hand-edited slug on the tenant row fetches nothing and the default look stands,
 * rather than requesting whatever path it spells.
 *
 * Appended to the END of <head>, after the app's own stylesheet in the built page and after
 * the style tags Vite injects in development, so a theme's plain :root wins on source order
 * alone. `registry` is for the tests; the app always uses the real one.
 */
export function applyTheme(slug, registry) {
  const href = themeHref(slug, registry)
  const existing = document.getElementById(THEME_LINK_ID)
  if (!href) {
    existing?.remove()
    return
  }
  if (existing?.getAttribute('href') === href) return
  const link = existing || document.createElement('link')
  link.id = THEME_LINK_ID
  link.rel = 'stylesheet'
  link.href = href
  // Re-appended even when it already existed, so it stays last if anything was added since.
  document.head.appendChild(link)
}

export const tintOf = (hex, t = 0.88) => {
  const rgb = parseHex(hex)
  return rgb ? toHex(mix(rgb, [255, 255, 255], t)) : '#f1f5f9'
}
